import { createContext, useEffect, useState } from "react";

export const CookieConsentContext = createContext({});

const DEFAULT_OPTIONS = {
  youtube: false,
  vimeo: false,
  facebook: false,
  tiktok: false,
  matomo: false,
  openstreetmap: false,
  studiassist: false,
};

export function CookieConsentProvider(props) {
  const [cookieConsent, setCookieConsent] = useState("undecided");
  const [options, setOptions] = useState(DEFAULT_OPTIONS);

  useEffect(() => {
    const stored = readStoredCookieOptions();
    setCookieConsent(getCookieConsent(stored));
    setOptions(getCookieOptions(stored));
  }, []);

  function updateCookieConsent(consent) {
    if (typeof window !== "undefined") {
      const opts = {
        youtube: consent,
        vimeo: consent,
        facebook: consent,
        tiktok: consent,
        matomo: consent,
        openstreetmap: consent,
        studiassist: consent,
      };
      setOptions(opts);
      setCookieConsent("decided");
      window.localStorage.setItem("cookieConsent", "decided");
      window.localStorage.setItem("cookieOptions", JSON.stringify(opts));
    }
  }

  const setOptionsKey = (key, value) => {
    if (key === "all") {
      updateCookieConsent(value);
    } else {
      const newOptions = { ...options, [key]: value };
      setOptions(newOptions);
      setCookieConsent("decided");
      window.localStorage.setItem("cookieConsent", "decided");
      window.localStorage.setItem("cookieOptions", JSON.stringify(newOptions));
    }
  };

  const optionAllowed = (key) => cookieConsent === "decided" && options[key];

  return (
    <CookieConsentContext.Provider
      value={{
        cookieConsent,
        options,
        optionAllowed,
        acceptCookieConsent: () => updateCookieConsent(true),
        declineCookieConsent: () => updateCookieConsent(false),
        setOptions: (key, value) => setOptionsKey(key, value),
      }}
    >
      {props.children}
    </CookieConsentContext.Provider>
  );
}

function readStoredCookieOptions() {
  try {
    return JSON.parse(window.localStorage.getItem("cookieOptions"));
  } catch {
    return null;
  }
}

// A service added after the visitor already decided (new key in
// DEFAULT_OPTIONS missing from their stored choice) means they were never
// asked about it, so treat consent as undecided again to re-show the banner.
function hasUndecidedOptions(stored) {
  if (!stored || typeof stored !== "object") return true;
  return Object.keys(DEFAULT_OPTIONS).some((key) => !(key in stored));
}

function getCookieConsent(stored) {
  if (window.localStorage.getItem("cookieConsent") !== "decided") {
    return "undecided";
  }
  return hasUndecidedOptions(stored) ? "undecided" : "decided";
}

function getCookieOptions(stored) {
  // Merge over defaults so options added after a visitor already decided
  // (e.g. studiassist) aren't silently undefined/falsy for them.
  return { ...DEFAULT_OPTIONS, ...stored };
}
